import React from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { 
  Bed, Home, Bath, Trees, ArrowUpCircle, 
  Car, Clock, PoundSterling, Brain, Heart 
} from "lucide-react";
import { keyFacts } from "../mock";

export const KeyFacts = () => {
  const getIcon = (label) => {
    const iconClass = "h-5 w-5";
    switch(label) {
      case "Total Beds": return <Bed className={iconClass} />;
      case "Room Types": return <Home className={iconClass} />;
      case "En-suite Rooms": return <Bath className={iconClass} />;
      case "Garden Access": return <Trees className={iconClass} />;
      case "Lift Access": return <ArrowUpCircle className={iconClass} />;
      case "Parking": return <Car className={iconClass} />;
      case "Visiting Hours": return <Clock className={iconClass} />;
      case "Funding Accepted": return <PoundSterling className={iconClass} />;
      case "Dementia Specialist": return <Brain className={iconClass} />;
      case "End-of-Life Care": return <Heart className={iconClass} />;
      default: return <Home className={iconClass} />;
    }
  };

  return (
    <section id="key-facts" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-teal-100 text-teal-700 hover:bg-teal-200">
              Quick Information
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Key Facts at a Glance
            </h2>
            <div className="w-24 h-1 bg-orange-500 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600">
              Everything you need to know in one place
            </p>
          </div>

          {/* Facts Grid */}
          <Card className="border-2 border-teal-200 shadow-xl">
            <CardContent className="pt-8 pb-8">
              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
                {keyFacts.map((fact, index) => (
                  <div
                    key={index}
                    className="flex flex-col items-center text-center p-4 rounded-lg hover:bg-teal-50 transition-colors duration-200"
                  >
                    <div className="bg-teal-100 text-teal-700 p-3 rounded-full mb-3">
                      {getIcon(fact.label)}
                    </div>
                    <div className="font-semibold text-gray-900 mb-1 text-sm">
                      {fact.label}
                    </div>
                    <div className="text-teal-700 font-bold text-lg">
                      {fact.value}
                    </div>
                  </div>
                ))}
              </div>

              {/* Additional Info */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="grid md:grid-cols-3 gap-6 text-center">
                  <div>
                    <div className="font-bold text-2xl text-teal-700 mb-2">CQC Good</div>
                    <div className="text-sm text-gray-600">Rated Good across all areas</div>
                  </div>
                  <div>
                    <div className="font-bold text-2xl text-orange-500 mb-2">24/7 Care</div>
                    <div className="text-sm text-gray-600">Round-the-clock support</div>
                  </div>
                  <div>
                    <div className="font-bold text-2xl text-teal-700 mb-2">Food Rating 5</div>
                    <div className="text-sm text-gray-600">Excellent hygiene standards</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Additional Features */}
          <div className="grid md:grid-cols-3 gap-6 mt-8">
            <Card className="border-none shadow-lg bg-gradient-to-br from-teal-50 to-teal-100">
              <CardContent className="pt-6 text-center">
                <div className="bg-white text-teal-600 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Brain className="h-8 w-8" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Specialist Care</h3>
                <p className="text-gray-700 text-sm">
                  Dementia-trained staff with specialist support
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg bg-gradient-to-br from-orange-50 to-orange-100">
              <CardContent className="pt-6 text-center">
                <div className="bg-white text-orange-600 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-8 w-8" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Person-Centred</h3>
                <p className="text-gray-700 text-sm">
                  Individual care plans tailored to each resident
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg bg-gradient-to-br from-blue-50 to-blue-100">
              <CardContent className="pt-6 text-center">
                <div className="bg-white text-blue-600 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Trees className="h-8 w-8" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Beautiful Setting</h3>
                <p className="text-gray-700 text-sm">
                  Landscaped gardens with safe outdoor access
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
